<script setup lang="ts">
import {
  onMounted,
  ref,
  watch,
} from 'vue';
import BottomSheet from '@/shared/components/core/BottomSheet.vue';
import { useI18n } from 'vue-i18n';
import { UniversalButtonColorType, UniversalButtonSizeType } from '@/shared/components/base/UniversalButton/types.ts';
import UniversalButton from '@/shared/components/base/UniversalButton/UniversalButton.vue';
import YoutubePlayer from '@/shared/components/base/YoutubePlayer/YoutubePlayer.vue';
import { storeToRefs } from 'pinia';
import { useMainStore } from '@/core/store/main.ts';
import { AppScreens } from '@/shared/types';
import { useExperiencePlayerStore } from '@/core/store/experience-player.ts';
import { useLeaveFeedbackReminder } from './useLeaveFeedbackReminder.ts';
import { useLoaderAtStartStore } from '@/shared/components/loaders/LoaderAtStartApp/store';

// показуємо popup відгука на 4-й день після того, як
// користувач зареєструвався в додатку, а потім кожні 7 днів
// активного користування додатком

const { t } = useI18n();

const popupVisible = ref(false);
const videoVisible = ref(false);

const { activeScreen } = storeToRefs(useMainStore());

const { visible: loaderVisible } = storeToRefs(useLoaderAtStartStore());

const {
  play: playExperience,
  pause: pauseExperience,
} = useExperiencePlayerStore();

const {
  today,
  isNewActiveDay,
  isTimeToRemindFeedback,
  savedData,
  saveData,
} = useLeaveFeedbackReminder();

const listOfAllowedSectionForShowPopup = [
  AppScreens.VideoBible,
  AppScreens.InteractiveBible,
  AppScreens.PrayerExperience,
  AppScreens.ReadingPlans,
  AppScreens.ReadingPlanDetail,
  AppScreens.ReadingPlanDay,
  AppScreens.Stories,
  AppScreens.Story,
];

const showPopup = () => {
  if (!listOfAllowedSectionForShowPopup.includes(activeScreen.value) || loaderVisible.value) {
    return;
  }

  videoVisible.value = true;
  setTimeout(() => {
    popupVisible.value = true;

    pauseExperience();

    savedData.value.lastPopupShowDate = today;
    saveData();

    unwatch();
  }, 2000);
};

const updateActiveDays = () => {
  savedData.value.userActiveDays.push(today);

  // Очищуємо старі записи, залишаючи лише останні 15 днів
  // Це число з запасом, щоб не зламати логіку 4 + 7 днів
  if (savedData.value.userActiveDays.length > 15) {
    savedData.value.userActiveDays = savedData.value.userActiveDays.slice(-15);
  }

  saveData();
};

onMounted(() => {
  if (savedData.value.feedbackAdded) {
    unwatch();

    return;
  }

  if (isNewActiveDay.value) {
    setTimeout(updateActiveDays, 20000);
  }

  // if you were already offered to leave a review today
  if (savedData.value.lastPopupShowDate === today) {
    unwatch();

    return;
  }

  if (isTimeToRemindFeedback.value) {
    setTimeout(showPopup, 20000);
  }
});

const unwatch = watch(() => activeScreen.value, (newValue) => {
  if (!listOfAllowedSectionForShowPopup.includes(newValue)) {
    return;
  }

  if (savedData.value.feedbackAdded) {
    unwatch();

    return;
  }

  if (savedData.value.lastPopupShowDate === today) {
    unwatch();

    return;
  }

  if (isTimeToRemindFeedback.value) {
    setTimeout(showPopup, 5000);
  }
});

const openLink = () => {
  savedData.value.feedbackAdded = true;
  saveData();

  popupVisible.value = false;
  videoVisible.value = false;

  unwatch();

  window.open('https://cutt.ly/GrGbd2Pn', '_blank');
};

const onClose = () => {
  popupVisible.value = false;
  videoVisible.value = false;

  playExperience();

  unwatch();
};

</script>

<template>
  <BottomSheet
    :visible="popupVisible"
    background="rgba(18, 18, 18, 1)"
    :max-height-precent="80"
    @close="onClose"
  >
    <template #static-content>
      <h1 class="title">
        {{ t('simpe_page.feedback.title') }}
      </h1>
    </template>
    <template #scrollable-content>
      <div class="main-content">
        <div class="video-container">
          <YoutubePlayer
            v-if="videoVisible"
            video-id="ZHDQN8ExVaw"
            manual-mute
          />
        </div>

        <div class="card-info">
          <div class="text">
            {{ t('simpe_page.feedback.text_1') }}
          </div>

          <UniversalButton
            ref="nextBtn$"
            :theme="UniversalButtonColorType.Gradient"
            :height="UniversalButtonSizeType.Large"
            :background-opacity="0.8"
            block
            uppercase
            font-size="16px"
            class="to-next-item-btn"
            @click="openLink"
          >
            <div
              class="to-next-item-btn-content"
            >
              {{ t('simpe_page.feedback.btn') }}
            </div>
          </UniversalButton>

          <div class="text-mini">
            <span>{{ t('simpe_page.feedback.text_2') }}</span>
            <a
              class="email"
              href="mailto:hey@ohmy.bible"
            > hey@ohmy.bible</a>
          </div>
        </div>
      </div>
    </template>
  </BottomSheet>
</template>

<style scoped>
.title {
  color: #D0D0D0;
  font-family: 'Poppins';
  font-size: 26px;
  font-style: normal;
  font-weight: 600;
  line-height: 34px; /* 130.769% */
  text-align: center;

  margin-top: 7px;
  margin-bottom: 20px;
  z-index: 5;
  position: relative;
}

.main-content {
  padding-left: 42px;
  padding-right: 42px;
}

.card-info {
  padding: 20px 16px 26px 16px;
}

.text {
  color: #D0D0D0;
  font-family: 'Poppins';
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: 24px; /* 150% */
  text-align: center;
}

.text-mini {
  color: #8B8B8A;
  text-align: center;
  font-family: 'Poppins';
  font-size: 11px;
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 218% */

  width: 100%;
}

.email {
  color: rgba(255, 176, 60, 0.8);
  transition: all 0.3s ease;

  &:hover {
    color: rgba(255, 176, 60, 1);
  }
}

.video-container {
  position: relative;
  border-radius: 8px;
  overflow: hidden;
}

.to-next-item-btn {
  z-index: 6;

  display: flex;
  align-items: center;
  justify-content: center;
  column-gap: 8px;
  margin-top: 24px;
  margin-bottom: 26px;

  animation: 5s linear infinite animBtnShadow;

  &::v-deep(.text) {
    width: 100%;
  }
}

@keyframes animBtnShadow {
  0% {
    filter: drop-shadow(0px 0px 1px #ca4b4c);
  }
  33% {
    filter: drop-shadow(0px 4px 70px #ca4b4c);
  }
  66% {
    filter: drop-shadow(0px 0px 1px #ca4b4c);
  }
}

.to-next-item-btn-content {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}
</style>
